import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, boolean, jsonb, pgEnum, integer } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const roleEnum = pgEnum("role", ["SUPER_ADMIN", "ADMIN", "PROVIDER", "MODERATOR", "USER"]);
export const languageEnum = pgEnum("language", ["ENGLISH", "SPANISH", "FRENCH", "GERMAN", "PORTUGUESE", "ITALIAN", "JAPANESE", "KOREAN", "CHINESE", "ARABIC", "HINDI", "NORWEGIAN", "TAGALOG", "OTHER"]);
export const sessionStatusEnum = pgEnum("session_status", ["SCHEDULED", "LIVE", "ENDED"]);
export const visibilityEnum = pgEnum("visibility", ["PUBLIC", "UNLISTED", "PRIVATE"]);
export const chatModeEnum = pgEnum("chat_mode", ["OPEN", "MOD_ONLY", "CLOSED"]);
export const recordingStatusEnum = pgEnum("recording_status", ["PENDING", "RECORDING", "PROCESSING", "READY", "FAILED", "PARTIAL"]);
export const userStatusEnum = pgEnum("user_status", ["ACTIVE", "DISABLED"]);

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  email: text("email").notNull().unique(),
  password: text("password").notNull(),
  role: roleEnum("role").notNull().default("USER"),
  status: userStatusEnum("status").notNull().default("ACTIVE"),
  avatarUrl: text("avatar_url"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const providerProfiles = pgTable("provider_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id).unique(),
  displayName: text("display_name").notNull(),
  bio: text("bio"),
  avatarUrl: text("avatar_url"),
  roomSlug: text("room_slug").unique(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const classes = pgTable("classes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  providerId: varchar("provider_id").notNull().references(() => users.id),
  title: text("title").notNull(),
  description: text("description"),
  language: languageEnum("language").notNull().default("ENGLISH"),
  thumbnailUrl: text("thumbnail_url"),
  category: text("category"),
  restrictedWords: text("restricted_words").array().notNull().default(sql`'{}'::text[]`),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const sessions = pgTable("sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  classId: varchar("class_id").notNull().references(() => classes.id),
  startsAt: timestamp("starts_at").notNull(),
  endsAt: timestamp("ends_at"),
  status: sessionStatusEnum("status").notNull().default("SCHEDULED"),
  livekitRoomName: text("livekit_room_name").notNull().unique(),
  visibility: visibilityEnum("visibility").notNull().default("PUBLIC"),
  chatMode: chatModeEnum("chat_mode").notNull().default("OPEN"),
  allowReactions: boolean("allow_reactions").notNull().default(true),
  allowViewerMic: boolean("allow_viewer_mic").notNull().default(false),
  allowViewerCam: boolean("allow_viewer_cam").notNull().default(false),
  autoRecord: boolean("auto_record").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const sessionModerators = pgTable("session_moderators", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull().references(() => sessions.id),
  userId: varchar("user_id").notNull().references(() => users.id),
  permissions: jsonb("permissions").notNull().default({
    canDeleteMessages: true,
    canMuteUsers: true,
    canKickUsers: true,
    canEndSession: false,
  }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const sessionAttendance = pgTable("session_attendance", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull().references(() => sessions.id),
  userId: varchar("user_id").notNull().references(() => users.id),
  joinedAt: timestamp("joined_at").defaultNow().notNull(),
  leftAt: timestamp("left_at"),
});

export const messages = pgTable("messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull().references(() => sessions.id),
  userId: varchar("user_id").notNull().references(() => users.id),
  text: text("text").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  deletedAt: timestamp("deleted_at"),
  deletedByUserId: varchar("deleted_by_user_id").references(() => users.id),
});

export const reactions = pgTable("reactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull().references(() => sessions.id),
  userId: varchar("user_id").notNull().references(() => users.id),
  type: text("type").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const recordings = pgTable("recordings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull().references(() => sessions.id),
  status: recordingStatusEnum("status").notNull().default("PENDING"),
  url: text("url"),
  title: text("title"),
  description: text("description"),
  tags: text("tags").array().notNull().default(sql`'{}'::text[]`),
  durationSeconds: integer("duration_seconds"),
  thumbnailUrl: text("thumbnail_url"),
  startedAt: timestamp("started_at"),
  endedAt: timestamp("ended_at"),
  isPublic: boolean("is_public").notNull().default(false),
  autoPublish: boolean("auto_publish").notNull().default(false),
  chunkCount: integer("chunk_count").notNull().default(0),
  chunkPaths: text("chunk_paths").array().notNull().default(sql`'{}'::text[]`),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const recordingTags = pgTable("recording_tags", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  providerId: varchar("provider_id").notNull().references(() => users.id),
  name: text("name").notNull(),
  color: text("color").notNull().default("#06b6d4"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const cohostStatusEnum = pgEnum("cohost_status", ["INVITED", "ACCEPTED", "DECLINED", "REMOVED"]);

export const sessionCohosts = pgTable("session_cohosts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull().references(() => sessions.id),
  userId: varchar("user_id").notNull().references(() => users.id),
  invitedByUserId: varchar("invited_by_user_id").notNull().references(() => users.id),
  status: cohostStatusEnum("status").notNull().default("INVITED"),
  permissions: jsonb("permissions").notNull().default({
    canPublishVideo: true,
    canPublishAudio: true,
    canScreenShare: true,
    canModerateChat: true,
  }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const approvalStatusEnum = pgEnum("approval_status", ["PENDING", "APPROVED", "REJECTED"]);

export const scheduleApprovals = pgTable("schedule_approvals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  requesterId: varchar("requester_id").notNull().references(() => users.id),
  targetType: text("target_type").notNull(),
  targetId: text("target_id").notNull(),
  changeType: text("change_type").notNull(),
  proposedChanges: jsonb("proposed_changes").notNull(),
  status: approvalStatusEnum("status").notNull().default("PENDING"),
  reviewerId: varchar("reviewer_id").references(() => users.id),
  reviewerNote: text("reviewer_note"),
  reviewedAt: timestamp("reviewed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const auditLogs = pgTable("audit_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  actorUserId: varchar("actor_user_id").references(() => users.id),
  action: text("action").notNull(),
  targetType: text("target_type").notNull(),
  targetId: text("target_id").notNull(),
  meta: jsonb("meta"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertUserSchema = createInsertSchema(users).omit({ id: true, createdAt: true });
export const loginSchema = z.object({ email: z.string().email(), password: z.string().min(6) });
export const registerSchema = z.object({
  name: z.string().min(2),
  email: z.string().email(),
  password: z.string().min(6),
});

export const insertClassSchema = createInsertSchema(classes).omit({ id: true, createdAt: true, providerId: true });
export const insertSessionSchema = createInsertSchema(sessions).omit({ id: true, createdAt: true, livekitRoomName: true, status: true, endsAt: true });
export const insertMessageSchema = z.object({ text: z.string().min(1).max(500) });
export const insertReactionSchema = z.object({ type: z.string().min(1).max(20) });
export const insertRecordingTagSchema = createInsertSchema(recordingTags).omit({ id: true, createdAt: true, providerId: true });

export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type Class = typeof classes.$inferSelect;
export type InsertClass = z.infer<typeof insertClassSchema>;
export type Session = typeof sessions.$inferSelect;
export type InsertSession = z.infer<typeof insertSessionSchema>;
export type Message = typeof messages.$inferSelect;
export type Reaction = typeof reactions.$inferSelect;
export type Recording = typeof recordings.$inferSelect;
export type AuditLog = typeof auditLogs.$inferSelect;
export type ProviderProfile = typeof providerProfiles.$inferSelect;
export type SessionModerator = typeof sessionModerators.$inferSelect;
export type SessionAttendance = typeof sessionAttendance.$inferSelect;
export type RecordingTag = typeof recordingTags.$inferSelect;
export type SessionCohost = typeof sessionCohosts.$inferSelect;
export type ScheduleApproval = typeof scheduleApprovals.$inferSelect;

export const LANGUAGES = [
  { value: "ENGLISH", label: "English", flag: "GB" },
  { value: "SPANISH", label: "Spanish", flag: "ES" },
  { value: "FRENCH", label: "French", flag: "FR" },
  { value: "GERMAN", label: "German", flag: "DE" },
  { value: "PORTUGUESE", label: "Portuguese", flag: "BR" },
  { value: "ITALIAN", label: "Italian", flag: "IT" },
  { value: "JAPANESE", label: "Japanese", flag: "JP" },
  { value: "KOREAN", label: "Korean", flag: "KR" },
  { value: "CHINESE", label: "Chinese", flag: "CN" },
  { value: "ARABIC", label: "Arabic", flag: "SA" },
  { value: "HINDI", label: "Hindi", flag: "IN" },
  { value: "NORWEGIAN", label: "Norsk", flag: "NO" },
  { value: "TAGALOG", label: "Tagalog", flag: "PH" },
  { value: "OTHER", label: "Other", flag: "UN" },
] as const;

export type Role = "SUPER_ADMIN" | "ADMIN" | "PROVIDER" | "MODERATOR" | "USER";

export const ROLE_HIERARCHY: Record<Role, number> = {
  SUPER_ADMIN: 4,
  ADMIN: 3,
  PROVIDER: 2,
  MODERATOR: 1,
  USER: 0,
};

export function hasMinRole(userRole: Role, requiredRole: Role): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

export function isAdminLevel(role: Role): boolean {
  return role === "SUPER_ADMIN" || role === "ADMIN";
}
export type Language = typeof languageEnum.enumValues[number];
export type SessionStatus = typeof sessionStatusEnum.enumValues[number];
export type Visibility = typeof visibilityEnum.enumValues[number];
export type ChatMode = typeof chatModeEnum.enumValues[number];
export type RecordingStatus = typeof recordingStatusEnum.enumValues[number];
export type UserStatus = typeof userStatusEnum.enumValues[number];
